import React from 'react';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { PageSlice, SliceTwoColumnImageAndTextDetails } from '@contracts';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import useReplacedInnerHtml from '@utils/useReplacedInnerHtml';
import { SFDBodyLG } from '@components/shared/Typography';
import TwoColumn from './TwoColumn';
import Text from './Text';
import useDesktop from './useDesktop';

interface Props {
  slice: PageSlice;
}

const BREAKPOINT = '1200px';

const TwoColumnImageAndText = ({ slice }: Props) => {
  const sliceDetails = slice.primary as SliceTwoColumnImageAndTextDetails;
  const { t } = useTranslation();
  const mainTextRef = useReplacedInnerHtml(t(sliceDetails?.main_text_key));
  const isMobile = !useDesktop(768);

  return (
    <Container $padding={sliceDetails?.padding_style} $background={sliceDetails?.background_style}>
      <TwoColumn equal breakpoint={BREAKPOINT}>
        <ImageWrapper>
          <LazyLoadImage src={sliceDetails?.image?.url} alt={sliceDetails?.image?.alt || ''} />
        </ImageWrapper>
        <TextWrapper>
          <MainText ref={mainTextRef} $bold />
          <Text text={t(sliceDetails?.secondary_text_key)} readMore={!isMobile} maxLength={71} />
        </TextWrapper>
      </TwoColumn>
    </Container>
  );
};

const Container = styled.div<{ $padding: string; $background: string }>`
  background: ${(props) => props.$background || Colors.ecru50};
  padding: ${(props) => props.$padding || '90px 120px'};
  color: ${Colors.navyBlue};

  & > .two-column {
    align-items: stretch;

    @media (max-width: ${BREAKPOINT}) {
      align-items: center;
    }
  }

  @media (max-width: ${BREAKPOINT}) {
    padding: 45px 60px;
  }

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const ImageWrapper = styled.div`
  max-width: 600px;
  border-radius: 5px 0 0 5px;
  overflow: hidden;

  & img {
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: ${BREAKPOINT}) {
    border-radius: 5px 5px 0 0;
  }
`;

const TextWrapper = styled.div`
  max-width: 600px;
  border-radius: 0 5px 5px 0;
  overflow: hidden;

  padding: 40px 60px;
  align-self: stretch;
  background: ${Colors.cream};
  justify-content: center;
  flex-shrink: 1;
  flex-grow: 0;
  min-width: 50%;

  @media (max-width: ${BREAKPOINT}) {
    align-self: auto;
    border-radius: 0 0 5px 5px;
  }

  @media (max-width: 768px) {
    padding: 20px 25px 30px 25px;
  }
`;

const MainText = styled(SFDBodyLG)`
  margin-bottom: 10px;
`;

export default TwoColumnImageAndText;
